import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ListboxModule } from 'primeng/listbox';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AyaCourseService } from '../../services';
import { CourseStore } from '../../store';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { GroupedAyaCourse } from '../../types';
import { SelectableField } from '@lx/quote-tool/lite/shared/types';
import { ListBoxComponent, SearcherComponent } from '@lx/quote-tool/lite/shared/ui/components';

@Component({
  standalone: true,
  selector: 'lx-aya-course-selector',
  imports: [SearcherComponent, ListboxModule, AsyncPipe, NgOptimizedImage, FormsModule, ListBoxComponent],
  templateUrl: './aya-course-selector.component.html',
  styleUrls: ['./aya-course-selector.component.scss'],
})
export class AyaCourseSelectorComponent implements OnInit {
  @Input() visible = false;
  @Output() selectedCourseChanged = new EventEmitter<SelectableField<CourseDetails>>();

  groupedCourses$: Observable<GroupedAyaCourse[]> | undefined;
  filteredGroupedCourses$: Observable<GroupedAyaCourse[]> | undefined;
  selectedCourseCode = '';

  private readonly ayaCourseService = inject(AyaCourseService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly courseStore = inject(CourseStore);

  ngOnInit(): void {
    this.groupedCourses$ = this.courseStore.ayaCourses$;
    this.filteredGroupedCourses$ = this.groupedCourses$;

    this.courseStore.selectedCourse$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selectedCourse: CourseDetails) => {
      this.selectedCourseCode = selectedCourse?.code || '';
    });
  }

  filterCourse(query: string) {
    if (!query) {
      this.filteredGroupedCourses$ = this.groupedCourses$;
      return;
    }
    this.filteredGroupedCourses$ = this.ayaCourseService.filterGroupedAyaCoursesByQuery(this.groupedCourses$, query);
  }

  triggerSelectedCourseChanged() {
    const courses$ = this.filteredGroupedCourses$ ?? this.groupedCourses$;

    if (!courses$) {
      return;
    }

    courses$
      .pipe(
        map((courses) => this.findSelectedCourse(courses)),
        filter((course): course is CourseDetails => !!course),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((course) => {
        this.selectedCourseChanged.emit({
          value: course,
          fromUser: true,
        });
      });
  }

  private findSelectedCourse(courses: GroupedAyaCourse[]): CourseDetails | undefined {
    for (const group of courses) {
      const courseItem = group.items.find((item) => item.value === this.selectedCourseCode);
      if (courseItem) {
        const { value: code, label: name } = courseItem;
        return {
          type: group.value,
          code,
          name,
        };
      }
    }
    return undefined;
  }
}
